import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  MousePointerClick, Globe, Search, RefreshCw, CheckCircle2, XCircle, AlertTriangle, Info,
  Copy, CheckCircle, Sparkles, History, Settings2, Trash2, ChevronRight, ShieldCheck, ExternalLink,
} from 'lucide-react';
import api from '../api';
import LoadingSpinner from './LoadingSpinner';
import Toast from './Toast';

/*
 * Co-Browsing admin page.
 *
 * Lists the agents with co-browse switched on and, for one agent, scans a
 * client website: a headless browser reads the site with the same page reader
 * the live widget uses, reports what the agent can and cannot do there, and an
 * AI review suggests fixes and site notes the admin can apply to the agent.
 * Backend: app/api/routes/cobrowse_scans.py.
 */

const cardClass = 'bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant';
const inputClass = 'w-full px-3 py-2 text-sm bg-muted/30 border border-border/60 rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50';
const btnPrimary = 'inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed';
const btnGhost = 'inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-border/60 text-sm text-foreground hover:bg-muted/40 disabled:opacity-50';

const SELECTED_KEY = 'cobrowse.selectedAgent';

const VERDICTS = {
  ready: { label: 'Ready to go live', tone: 'success', icon: CheckCircle2 },
  ready_with_fixes: { label: 'Works, with fixes recommended', tone: 'warning', icon: AlertTriangle },
  not_ready: { label: 'Not ready: fix the critical items first', tone: 'destructive', icon: XCircle },
};

const TONES = {
  success: 'border-success/40 bg-success/10 text-success',
  warning: 'border-warning/40 bg-warning/10 text-warning',
  destructive: 'border-destructive/40 bg-destructive/10 text-destructive',
  muted: 'border-border/60 bg-muted/30 text-muted-foreground',
};

const SEVERITY = {
  critical: { label: 'Critical', tone: 'destructive', icon: XCircle },
  warning: { label: 'Warning', tone: 'warning', icon: AlertTriangle },
  info: { label: 'Info', tone: 'muted', icon: Info },
};

const CAP_STATUS = {
  yes: { label: 'Works', tone: 'success', icon: CheckCircle2 },
  partial: { label: 'Partly', tone: 'warning', icon: AlertTriangle },
  no: { label: 'No', tone: 'destructive', icon: XCircle },
  caller: { label: 'Caller does it', tone: 'muted', icon: ShieldCheck },
};

const WHO = { client: 'Client website', braincx: 'BrainCX settings', agent_prompt: 'Agent prompt' };

const Pill = ({ tone = 'muted', children }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${TONES[tone]}`}>{children}</span>
);

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso + (iso.endsWith('Z') ? '' : 'Z')).toLocaleString(); } catch { return iso; }
};

const errorText = (err, fallback) => err?.response?.data?.detail || err?.message || fallback;

// ─── Agent list ─────────────────────────────────────────────────────────────

const AgentList = ({ agents, selectedId, onSelect, showAll, setShowAll }) => {
  const visible = showAll ? agents : agents.filter((a) => a.cobrowse_enabled);
  return (
    <div className={`${cardClass} p-4`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-foreground">Agents</h3>
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} className="accent-[hsl(var(--primary))]" />
          Show all
        </label>
      </div>
      {visible.length === 0 && (
        <p className="text-xs text-muted-foreground">
          No agent has co-browse switched on yet. Tick Show all to pick one and switch it on in Setup.
        </p>
      )}
      <div className="space-y-2">
        {visible.map((a) => {
          const verdict = a.last_scan?.verdict && VERDICTS[a.last_scan.verdict];
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => onSelect(a.id)}
              className={`w-full text-left rounded-lg border p-3 transition-colors ${selectedId === a.id ? 'border-primary bg-primary/10' : 'border-border/60 hover:border-primary/40'}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-foreground truncate">{a.name}</span>
                {a.cobrowse_enabled ? <Pill tone="success">On</Pill> : <Pill>Off</Pill>}
              </div>
              <div className="mt-1 text-xs text-muted-foreground truncate">
                {(a.cobrowse_allowed_domains || '').split(/[\n,]/).filter(Boolean).join(', ') || 'Any website'}
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5 text-xs text-muted-foreground">
                <span>{a.calls_30d} call{a.calls_30d === 1 ? '' : 's'} in 30 days</span>
                {a.action_success_rate != null && <span>· {a.action_success_rate}% actions OK</span>}
                {a.agent_type === 'flow' && <Pill tone="warning">Flow agent</Pill>}
              </div>
              {verdict && (
                <div className="mt-2"><Pill tone={verdict.tone}>Last scan: {verdict.label}</Pill></div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ─── Setup tab ──────────────────────────────────────────────────────────────

const SetupTab = ({ agent, frontendUrl, onSaved, notify }) => {
  const [enabled, setEnabled] = useState(agent.cobrowse_enabled);
  const [domains, setDomains] = useState(agent.cobrowse_allowed_domains || '');
  const [notes, setNotes] = useState(agent.cobrowse_site_notes || '');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setEnabled(agent.cobrowse_enabled);
    setDomains(agent.cobrowse_allowed_domains || '');
    setNotes(agent.cobrowse_site_notes || '');
  }, [agent.id, agent.cobrowse_enabled, agent.cobrowse_allowed_domains, agent.cobrowse_site_notes]);

  const host = frontendUrl || window.location.origin;
  const snippet = `<script src="${host}/cobrowse-widget.js?agent=${agent.id}" async></script>`;
  // The Interactive Avatar Panel runs the same co-browse with a live avatar
  const avatarSnippet = agent.liveavatar_enabled
    ? `<script src="${host}/avatar-panel-v2.js?agent=${agent.id}&cobrowse=1" async></script>`
    : null;
  const [copiedAvatar, setCopiedAvatar] = useState(false);
  const dirty = enabled !== agent.cobrowse_enabled
    || domains !== (agent.cobrowse_allowed_domains || '')
    || notes !== (agent.cobrowse_site_notes || '');

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        cobrowse_enabled: enabled,
        cobrowse_allowed_domains: domains.trim() || null,
        cobrowse_site_notes: notes.trim() || null,
      };
      await api.put(`/agents/${agent.id}`, payload);
      onSaved(payload);
      notify('Co-browse settings saved', 'success');
    } catch (err) {
      notify(errorText(err, 'Could not save settings'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {agent.agent_type === 'flow' && (
        <div className={`rounded-lg border p-3 text-sm ${TONES.warning}`}>
          Co-browse works with prompt agents only. Conversation flow agents are not supported yet.
        </div>
      )}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h4 className="text-sm font-semibold text-foreground">Allow page control</h4>
          <p className="text-xs text-muted-foreground mt-1">
            Calls from the co-browse widget can scroll, open pages on the same site, fill fields and submit forms after the caller says yes.
            Other widgets and phone calls are not affected.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          disabled={agent.agent_type === 'flow'}
          onClick={() => setEnabled((v) => !v)}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${enabled ? 'bg-primary' : 'bg-muted'}`}
        >
          <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
        </button>
      </div>

      <div>
        <h4 className="text-sm font-semibold text-foreground mb-1">Allowed websites</h4>
        <textarea value={domains} onChange={(e) => setDomains(e.target.value)} rows={3} className={`${inputClass} font-mono`} placeholder={'example.com\nshop.example.org'} />
        <p className="text-xs text-muted-foreground mt-1">One domain per line, subdomains included. Empty allows any site.</p>
      </div>

      <div>
        <h4 className="text-sm font-semibold text-foreground mb-1">Site notes</h4>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={8} className={inputClass} placeholder="Key pages, what each form is for, what the caller must do themselves. A website scan can draft these." />
        <p className="text-xs text-muted-foreground mt-1">Added to this agent&apos;s prompt on co-browse calls only.</p>
      </div>

      <div className="flex justify-end">
        <button type="button" onClick={save} disabled={!dirty || saving} className={btnPrimary}>
          {saving ? 'Saving…' : 'Save settings'}
        </button>
      </div>

      <div>
        <h4 className="text-sm font-semibold text-foreground mb-1">Install on the client website</h4>
        <p className="text-xs text-muted-foreground mb-2">Add this to every page, for example in the site header.</p>
        <div className="flex gap-2">
          <pre className="flex-1 p-3 bg-muted/40 border border-border/60 rounded-lg font-mono text-xs text-foreground whitespace-pre-wrap break-all">{snippet}</pre>
          <button
            type="button"
            className={`${btnGhost} self-start`}
            onClick={async () => {
              try { await navigator.clipboard.writeText(snippet); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { notify('Failed to copy', 'error'); }
            }}
          >
            {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        {avatarSnippet && (
          <>
            <p className="text-xs text-muted-foreground mt-3 mb-2">
              Or, with the Interactive Avatar Panel (live avatar, transcript, voice and typing) as the call interface:
            </p>
            <div className="flex gap-2">
              <pre className="flex-1 p-3 bg-muted/40 border border-border/60 rounded-lg font-mono text-xs text-foreground whitespace-pre-wrap break-all">{avatarSnippet}</pre>
              <button
                type="button"
                className={`${btnGhost} self-start`}
                onClick={async () => {
                  try { await navigator.clipboard.writeText(avatarSnippet); setCopiedAvatar(true); setTimeout(() => setCopiedAvatar(false), 2000); } catch { notify('Failed to copy', 'error'); }
                }}
              >
                {copiedAvatar ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copiedAvatar ? 'Copied' : 'Copy'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Report ─────────────────────────────────────────────────────────────────

const Section = ({ title, icon: Icon, children, aside }) => (
  <div className="rounded-lg border border-border/60 p-4">
    <div className="flex items-center justify-between gap-2 mb-3">
      <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">{Icon && <Icon className="w-4 h-4 text-primary" />}{title}</h4>
      {aside}
    </div>
    {children}
  </div>
);

const ReviewPanel = ({ scan, agent, onApplyNotes, onRerun, rerunning }) => {
  const review = scan.review;
  const [notes, setNotes] = useState(review?.site_notes || '');
  useEffect(() => { setNotes(review?.site_notes || ''); }, [review?.site_notes]);

  if (scan.review_status === 'skipped') {
    return (
      <Section title="AI review" icon={Sparkles} aside={<button type="button" className={btnGhost} onClick={onRerun}>Run AI review</button>}>
        <p className="text-xs text-muted-foreground">The AI review was not run for this scan.</p>
      </Section>
    );
  }
  if (['queued', 'running'].includes(scan.review_status)) {
    return (
      <Section title="AI review" icon={Sparkles}>
        <LoadingSpinner size="sm" text="Reviewing the scan and the agent's prompt…" />
      </Section>
    );
  }
  if (scan.review_status === 'failed' || review?.error) {
    return (
      <Section title="AI review" icon={Sparkles} aside={<button type="button" className={btnGhost} onClick={onRerun} disabled={rerunning}>Try again</button>}>
        <p className="text-xs text-destructive">The AI review failed: {review?.error || 'unknown error'}</p>
      </Section>
    );
  }
  if (!review) return null;

  return (
    <div className="space-y-4">
      {review.summary && (
        <Section
          title="AI review"
          icon={Sparkles}
          aside={<button type="button" className={btnGhost} onClick={onRerun} disabled={rerunning}><RefreshCw className="w-4 h-4" />Re-run</button>}
        >
          <p className="text-sm text-foreground leading-relaxed">{review.summary}</p>
          <p className="text-xs text-muted-foreground mt-2">Reviewed by {review.model}{review.cost_usd != null ? ` · about $${review.cost_usd}` : ''}</p>
        </Section>
      )}

      {review.critical_fixes?.length > 0 && (
        <Section title="Critical fixes" icon={XCircle}>
          <ul className="space-y-2">
            {review.critical_fixes.map((f, i) => (
              <li key={i} className={`rounded-lg border p-3 ${TONES.destructive}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold">{f.title}</span>
                  <Pill tone="destructive">{WHO[f.who] || f.who}</Pill>
                </div>
                <p className="text-xs text-foreground mt-1">{f.detail}</p>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {(review.can_do?.length > 0 || review.cannot_do?.length > 0) && (
        <div className="grid md:grid-cols-2 gap-4">
          <Section title="What the agent can do here" icon={CheckCircle2}>
            <ul className="space-y-1.5 text-sm text-foreground">
              {review.can_do.map((t, i) => <li key={i} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />{t}</li>)}
            </ul>
          </Section>
          <Section title="What it cannot do" icon={XCircle}>
            <ul className="space-y-1.5 text-sm text-foreground">
              {review.cannot_do.map((t, i) => <li key={i} className="flex gap-2"><XCircle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />{t}</li>)}
            </ul>
          </Section>
        </div>
      )}

      {review.warnings?.length > 0 && (
        <Section title="Warnings" icon={AlertTriangle}>
          <ul className="space-y-2">
            {review.warnings.map((w, i) => (
              <li key={i} className="text-sm"><span className="font-medium text-foreground">{w.title}.</span> <span className="text-muted-foreground">{w.detail}</span></li>
            ))}
          </ul>
        </Section>
      )}

      {review.prompt_fixes?.length > 0 && (
        <Section title="Prompt fixes" icon={Settings2}>
          <ul className="space-y-2">
            {review.prompt_fixes.map((p, i) => (
              <li key={i} className="rounded-lg border border-border/60 p-3">
                <p className="text-sm text-foreground">{p.issue}</p>
                <p className="text-xs text-muted-foreground mt-1"><span className="font-medium text-foreground">Fix:</span> {p.fix}</p>
              </li>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground mt-2">Edit the agent&apos;s prompt in Voice Agents, then re-run the review.</p>
        </Section>
      )}

      {review.site_notes && (
        <Section
          title="Suggested site notes"
          icon={Globe}
          aside={agent && (
            <button type="button" className={btnPrimary} onClick={() => onApplyNotes(notes)} disabled={!notes.trim()}>
              Apply to {agent.name}
            </button>
          )}
        >
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={10} className={inputClass} />
          <p className="text-xs text-muted-foreground mt-1">
            Applying replaces this agent&apos;s current site notes. They are added to its prompt on co-browse calls only. You can edit them first.
          </p>
        </Section>
      )}

      {review.test_script?.length > 0 && (
        <Section title="Live test call script" icon={MousePointerClick}>
          <ol className="list-decimal pl-5 space-y-1 text-sm text-foreground">
            {review.test_script.map((t, i) => <li key={i}>{t}</li>)}
          </ol>
        </Section>
      )}
    </div>
  );
};

const ReportView = ({ scan, agent, onApplyNotes, onRerun, rerunning }) => {
  const report = scan.report;
  if (!report) return null;
  const review = scan.review && !scan.review.error ? scan.review : null;
  const issues = report.summary?.issues || {};
  const verdictKey = review?.verdict || (issues.critical ? 'not_ready' : issues.warning ? 'ready_with_fixes' : 'ready');
  const verdict = VERDICTS[verdictKey];
  const VerdictIcon = verdict.icon;
  const install = report.install || {};

  return (
    <div className="space-y-4">
      <div className={`rounded-xl border p-4 flex items-start gap-3 ${TONES[verdict.tone]}`}>
        <VerdictIcon className="w-6 h-6 flex-shrink-0" />
        <div>
          <div className="font-semibold">{verdict.label}</div>
          <div className="text-xs text-foreground mt-1">
            {report.summary.pages_scanned} page{report.summary.pages_scanned === 1 ? '' : 's'} read
            {report.summary.pages_failed ? `, ${report.summary.pages_failed} failed` : ''}
            {' · '}{issues.critical || 0} critical, {issues.warning || 0} warnings
            {' · '}{report.site?.navigation === 'single_page' ? 'Single-page site' : 'Multi-page site'}
            {report.site?.platforms?.length ? ` (${report.site.platforms.join(', ')})` : ''}
          </div>
        </div>
      </div>

      <ReviewPanel scan={scan} agent={agent} onApplyNotes={onApplyNotes} onRerun={onRerun} rerunning={rerunning} />

      <Section title="Capabilities on this site" icon={MousePointerClick}>
        <div className="divide-y divide-border/60">
          {report.capabilities.map((c) => {
            const s = CAP_STATUS[c.status] || CAP_STATUS.no;
            const Icon = s.icon;
            return (
              <div key={c.tool} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <div className="text-sm text-foreground">{c.label}</div>
                  <div className="text-xs text-muted-foreground">{c.detail}</div>
                </div>
                <Pill tone={s.tone}><Icon className="w-3.5 h-3.5" />{s.label}</Pill>
              </div>
            );
          })}
        </div>
      </Section>

      <Section title="Installation" icon={ShieldCheck}>
        <div className="grid sm:grid-cols-2 gap-2 text-sm">
          {[
            ['Script tag on scanned pages', `${install.loader_pages} of ${install.pages}`, install.pages > 0 && install.loader_pages === install.pages],
            ['Co-browse switched on', install.agent_enabled ? 'Yes' : 'No', install.agent_enabled],
            ['Website in allowed list', install.domain_allowed ? (install.allowed_domains?.length ? 'Yes' : 'Any site allowed') : 'No', install.domain_allowed],
            ['Security policy allows our script', install.csp_blocks_script ? 'Blocked' : 'Yes', !install.csp_blocks_script],
            ['Security policy allows our call window', install.csp_blocks_frame ? 'Blocked' : 'Yes', !install.csp_blocks_frame],
            ['Microphone allowed for the widget', install.mic_blocked ? 'Blocked' : 'Yes', !install.mic_blocked],
          ].map(([label, value, ok]) => (
            <div key={label} className="flex items-center justify-between gap-2 rounded-lg bg-muted/30 px-3 py-2">
              <span className="text-muted-foreground">{label}</span>
              <span className={`font-medium ${ok ? 'text-success' : 'text-destructive'}`}>{value}</span>
            </div>
          ))}
        </div>
      </Section>

      {report.issues?.length > 0 && (
        <Section title="Findings" icon={AlertTriangle}>
          <ul className="space-y-2">
            {report.issues.map((issue) => {
              const sev = SEVERITY[issue.severity] || SEVERITY.info;
              const Icon = sev.icon;
              return (
                <li key={issue.key} className="rounded-lg border border-border/60 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-sm font-medium text-foreground">{issue.title}</span>
                    <Pill tone={sev.tone}><Icon className="w-3.5 h-3.5" />{sev.label}</Pill>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{issue.detail}</p>
                  {issue.pages?.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-1 break-all">Pages: {issue.pages.join(', ')}</p>
                  )}
                </li>
              );
            })}
          </ul>
        </Section>
      )}

      <Section title="Pages read" icon={Globe}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground">
                <th className="py-1 pr-3 font-medium">Page</th>
                <th className="py-1 pr-3 font-medium">Script</th>
                <th className="py-1 pr-3 font-medium">Links</th>
                <th className="py-1 pr-3 font-medium">Forms</th>
                <th className="py-1 font-medium">Search</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {report.pages.map((p) => (
                <tr key={p.url} className="align-top">
                  <td className="py-2 pr-3">
                    <div className="text-foreground break-all">{p.title || p.path}</div>
                    <div className="text-xs text-muted-foreground break-all">{p.path}</div>
                    {p.error && <div className="text-xs text-destructive">{p.error}</div>}
                  </td>
                  <td className="py-2 pr-3">{p.error ? '–' : p.loader ? <CheckCircle2 className="w-4 h-4 text-success" /> : <XCircle className="w-4 h-4 text-destructive" />}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{p.counts?.links ?? '–'}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{p.counts?.forms ?? '–'}</td>
                  <td className="py-2 text-muted-foreground">{p.error ? '–' : p.search ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
};

// ─── Scan tab ───────────────────────────────────────────────────────────────

const ScanTab = ({ agent, status, scanId, setScanId, onAgentChanged, notify }) => {
  const [url, setUrl] = useState('');
  const [maxPages, setMaxPages] = useState(status?.default_pages || 10);
  // Sent with the scan only; never stored (Shopify development stores)
  const [sitePassword, setSitePassword] = useState('');
  const [scan, setScan] = useState(null);
  const [starting, setStarting] = useState(false);
  const [rerunning, setRerunning] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    const domain = (agent.cobrowse_allowed_domains || '').split(/[\n,]/).map((d) => d.trim()).filter(Boolean)[0];
    setUrl(agent.last_scan?.url || (domain ? `https://${domain}` : ''));
  }, [agent.id]);

  const load = useCallback(async (id) => {
    clearTimeout(pollRef.current);
    if (!id) { setScan(null); return; }
    try {
      const { data } = await api.get(`/cobrowse/scans/${id}`);
      setScan(data);
      const busy = ['queued', 'running'].includes(data.status) || ['queued', 'running'].includes(data.review_status);
      if (busy) pollRef.current = setTimeout(() => load(id), 2500);
      else onAgentChanged();
    } catch (err) {
      if (err?.response?.status === 404) { setScan(null); setScanId(null); return; }
      pollRef.current = setTimeout(() => load(id), 5000);
    }
  }, [onAgentChanged, setScanId]);

  useEffect(() => { load(scanId); return () => clearTimeout(pollRef.current); }, [scanId, load]);

  const start = async () => {
    setStarting(true);
    try {
      const { data } = await api.post('/cobrowse/scans', {
        agent_id: agent.id, url, max_pages: Number(maxPages), run_review: true,
        ...(sitePassword.trim() ? { site_password: sitePassword.trim() } : {}),
      });
      setScanId(data.id);
    } catch (err) {
      notify(errorText(err, 'Could not start the scan'), 'error');
    } finally {
      setStarting(false);
    }
  };

  const rerun = async () => {
    setRerunning(true);
    try {
      await api.post(`/cobrowse/scans/${scan.id}/review`);
      load(scan.id);
    } catch (err) {
      notify(errorText(err, 'Could not start the review'), 'error');
    } finally {
      setRerunning(false);
    }
  };

  const applyNotes = async (notes) => {
    try {
      await api.put(`/agents/${agent.id}`, { cobrowse_site_notes: notes.trim() || null });
      notify(`Site notes applied to ${agent.name}`, 'success');
      onAgentChanged();
    } catch (err) {
      notify(errorText(err, 'Could not apply the notes'), 'error');
    }
  };

  const running = scan && ['queued', 'running'].includes(scan.status);

  return (
    <div className="space-y-5">
      {!status?.scanner_available && (
        <div className={`rounded-lg border p-3 text-sm ${TONES.warning}`}>
          The website scanner is not installed on this server (Playwright with Chromium). Ask an engineer to deploy the updated API image.
        </div>
      )}
      <div>
        <h4 className="text-sm font-semibold text-foreground mb-1">Scan a client website</h4>
        <p className="text-xs text-muted-foreground mb-3">
          A browser reads the site exactly as this agent would, without clicking or submitting anything,
          then an AI review checks the results against the agent&apos;s prompt. Only scan sites you have permission to test.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Globe className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://client-website.com" className={`${inputClass} pl-9`} />
          </div>
          <select value={maxPages} onChange={(e) => setMaxPages(e.target.value)} className={`${inputClass} sm:w-36`}>
            {[5, 10, 15, 20].filter((n) => n <= (status?.max_pages || 20)).map((n) => <option key={n} value={n}>{n} pages</option>)}
            <option value={0}>All pages</option>
          </select>
          <button type="button" onClick={start} disabled={!url.trim() || starting || running || !status?.scanner_available} className={btnPrimary}>
            <Search className="w-4 h-4" />{starting ? 'Starting…' : 'Scan website'}
          </button>
        </div>
        <div className="mt-2 sm:w-80">
          <input
            type="password"
            autoComplete="off"
            value={sitePassword}
            onChange={(e) => setSitePassword(e.target.value)}
            placeholder="Store password (optional)"
            className={inputClass}
          />
          <p className="text-xs text-muted-foreground mt-1">
            For a Shopify store still in development, which shows a password page to every visitor. Used for this scan only and never saved.
          </p>
        </div>
      </div>

      {scan && running && (
        <div className="rounded-lg border border-border/60 p-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-foreground">{scan.stage || 'Working…'}</span>
            <span className="text-muted-foreground">
              {scan.max_pages === 0 ? `${scan.pages_scanned} pages read` : `${scan.pages_scanned} / ${scan.max_pages}`}
            </span>
          </div>
          <div className="w-full h-2 rounded-full bg-muted/50 overflow-hidden">
            {scan.max_pages === 0 ? (
              <div className="h-full w-1/3 bg-primary rounded-full animate-pulse" />
            ) : (
              <div className="h-full bg-primary transition-all duration-500" style={{ width: `${Math.max(5, Math.round(100 * scan.pages_scanned / Math.max(1, scan.max_pages)))}%` }} />
            )}
          </div>
          {scan.max_pages === 0 && (
            <p className="text-xs text-muted-foreground mt-2">
              All pages: reads every page it can find on the site{status?.all_pages_cap ? `, up to ${status.all_pages_cap}` : ''}. Large sites take a while; you can leave this page and come back.
            </p>
          )}
        </div>
      )}

      {scan && scan.status === 'failed' && (
        <div className={`rounded-lg border p-3 text-sm ${TONES.destructive}`}>{scan.error || 'The scan failed.'}</div>
      )}

      {scan && scan.status === 'done' && (
        <>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span className="break-all">
              {scan.url}{' '}
              <a href={scan.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline"><ExternalLink className="w-3 h-3" />open</a>
            </span>
            <span>{fmtDate(scan.finished_at)}</span>
          </div>
          <ReportView scan={scan} agent={agent} onApplyNotes={applyNotes} onRerun={rerun} rerunning={rerunning} />
        </>
      )}
    </div>
  );
};

// ─── History tab ────────────────────────────────────────────────────────────

const HistoryTab = ({ agent, onOpen, notify }) => {
  const [scans, setScans] = useState(null);
  const refresh = useCallback(() => {
    api.get('/cobrowse/scans', { params: { agent_id: agent.id } })
      .then((r) => setScans(r.data.scans))
      .catch((err) => { setScans([]); notify(errorText(err, 'Could not load scans'), 'error'); });
  }, [agent.id, notify]);
  useEffect(() => { refresh(); }, [refresh]);

  if (scans === null) return <LoadingSpinner size="sm" />;
  if (!scans.length) return <p className="text-sm text-muted-foreground">No scans yet for this agent.</p>;
  return (
    <ul className="divide-y divide-border/60">
      {scans.map((s) => {
        const verdict = s.verdict && VERDICTS[s.verdict];
        return (
          <li key={s.id} className="py-3 flex items-center justify-between gap-3">
            <button type="button" onClick={() => onOpen(s.id)} className="flex-1 min-w-0 text-left group">
              <div className="text-sm text-foreground truncate group-hover:text-primary">{s.url}</div>
              <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap gap-2 items-center">
                <span>{fmtDate(s.created_at)}</span>
                {s.status !== 'done' && <Pill tone={s.status === 'failed' ? 'destructive' : 'muted'}>{s.status}</Pill>}
                {verdict && <Pill tone={verdict.tone}>{verdict.label}</Pill>}
                {s.issues && <span>{s.issues.critical || 0} critical · {s.issues.warning || 0} warnings</span>}
              </div>
            </button>
            <button
              type="button"
              title="Delete scan"
              className="p-2 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10"
              onClick={async () => {
                try { await api.delete(`/cobrowse/scans/${s.id}`); refresh(); } catch (err) { notify(errorText(err, 'Could not delete'), 'error'); }
              }}
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </li>
        );
      })}
    </ul>
  );
};

// ─── Page ───────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'scan', label: 'Website scan', icon: Search },
  { id: 'setup', label: 'Setup', icon: Settings2 },
  { id: 'history', label: 'History', icon: History },
];

const CobrowseManager = () => {
  const [agents, setAgents] = useState(null);
  const [status, setStatus] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [selectedId, setSelectedId] = useState(() => { try { return localStorage.getItem(SELECTED_KEY); } catch { return null; } });
  const [tab, setTab] = useState('scan');
  const [scanIds, setScanIds] = useState({});
  const [toast, setToast] = useState(null);

  const notify = useCallback((message, type = 'success') => setToast({ message, type }), []);

  const loadAgents = useCallback(async () => {
    try {
      const { data } = await api.get('/cobrowse/admin/agents');
      setAgents(data.agents);
    } catch (err) {
      setAgents([]);
      notify(errorText(err, 'Could not load agents'), 'error');
    }
  }, [notify]);

  useEffect(() => {
    loadAgents();
    api.get('/cobrowse/admin/status').then((r) => setStatus(r.data)).catch(() => setStatus({ scanner_available: false }));
  }, [loadAgents]);

  // Pick a sensible agent once the list arrives
  useEffect(() => {
    if (!agents || !agents.length) return;
    if (!agents.some((a) => a.id === selectedId)) {
      const first = agents.find((a) => a.cobrowse_enabled) || agents[0];
      setSelectedId(first.id);
      if (!first.cobrowse_enabled) setShowAll(true);
    } else if (!agents.find((a) => a.id === selectedId).cobrowse_enabled) {
      setShowAll(true);
    }
  }, [agents]);

  useEffect(() => {
    if (!selectedId) return;
    try { localStorage.setItem(SELECTED_KEY, selectedId); } catch { /* storage blocked */ }
  }, [selectedId]);

  const agent = agents?.find((a) => a.id === selectedId) || null;
  const agentId = agent?.id;
  const lastScanId = agent?.last_scan?.id || null;
  const scanId = agentId ? (scanIds[agentId] !== undefined ? scanIds[agentId] : lastScanId) : null;
  // Keyed on the id, not the agent object: the list reloads after each scan
  // and a new object here would restart the scan polling.
  const setScanId = useCallback((id) => { if (agentId) setScanIds((m) => ({ ...m, [agentId]: id })); }, [agentId]);

  const patchAgent = (patch) => setAgents((list) => list.map((a) => (a.id === selectedId ? { ...a, ...patch } : a)));

  if (agents === null) return <LoadingSpinner text="Loading co-browsing agents…" className="py-24" />;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
          <MousePointerClick className="w-5 h-5 text-primary-foreground" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-foreground">Co-Browsing</h2>
          <p className="text-sm text-muted-foreground">Agents that can guide callers around a client&apos;s website, and readiness checks for each site.</p>
        </div>
      </div>

      {status && !status.ai_available && (
        <div className={`rounded-lg border p-3 text-sm ${TONES.warning}`}>No AI provider key is configured, so scans run without the AI review.</div>
      )}

      <div className="grid lg:grid-cols-[320px_1fr] gap-6 items-start">
        <AgentList agents={agents} selectedId={selectedId} onSelect={(id) => { setSelectedId(id); setTab('scan'); }} showAll={showAll} setShowAll={setShowAll} />

        {agent ? (
          <div className={cardClass}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
              <div>
                <h3 className="text-lg font-semibold text-foreground">{agent.name}</h3>
                <p className="text-xs text-muted-foreground">
                  {agent.cobrowse_enabled ? 'Co-browse is on' : 'Co-browse is off'} · {agent.calls_30d} co-browse call{agent.calls_30d === 1 ? '' : 's'} in 30 days
                  {agent.action_success_rate != null ? ` · ${agent.action_success_rate}% of ${agent.actions_30d} page actions succeeded` : ''}
                </p>
              </div>
              <div className="flex gap-1 rounded-lg bg-muted/40 p-1">
                {TABS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTab(t.id)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm ${tab === t.id ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    <t.icon className="w-4 h-4" />{t.label}
                  </button>
                ))}
              </div>
            </div>

            {tab === 'scan' && (
              <ScanTab agent={agent} status={status} scanId={scanId} setScanId={setScanId} onAgentChanged={loadAgents} notify={notify} />
            )}
            {tab === 'setup' && (
              <SetupTab agent={agent} frontendUrl={status?.frontend_url} onSaved={patchAgent} notify={notify} />
            )}
            {tab === 'history' && (
              <HistoryTab agent={agent} onOpen={(id) => { setScanId(id); setTab('scan'); }} notify={notify} />
            )}
          </div>
        ) : (
          <div className={cardClass}><p className="text-sm text-muted-foreground">No agents found.</p></div>
        )}
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default CobrowseManager;
